import { isEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { SpDropdownToggle } from 'components/shared/Dropdown';
import { TinySpinner } from 'components/shared/TinySpinner/TinySpinner';
import { FC, PropsWithChildren, useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import type { RegulationDocumentStatus } from 'state/quotes/dashboard/model';
import { canBeDownloaded } from 'state/quotes/dashboard/model';

export const RegulationIndicator: FC<
  PropsWithChildren<{
    label: string;
    status: RegulationDocumentStatus;
    className?: string;
  }>
> = ({ label, status, className }) => {
  const { formatMessage } = useIntl();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(prevState => !prevState);

  if (status === 'NotAvailable') {
    return null;
  }

  return (
    <>
      {canBeDownloaded(status) && status.status === 'Success' && status.links.length > 1 && (
        <Dropdown show={dropdownOpen} onToggle={toggle}>
          <SpDropdownToggle
            caret={false}
            variant="link"
            className="btn btn-icon-start btn-flat text-uppercase"
            color=""
          >
            <em className="icon mx-1 text-primary">check_circle_outline</em>
            <span className="text-primary me-2">{label}</span>
            {dropdownOpen && <em className="icon text-primary">expand_more</em>}
            {!dropdownOpen && <em className="icon text-primary">chevron_right</em>}
          </SpDropdownToggle>
          <DropdownMenu>
            {status.links.map(([lang, uri]) => (
              <DropdownItem key={lang} href={uri} className="text-decoration-none" target="_blank">
                {formatMessage({ id: `field.${lang}` as any, defaultMessage: lang })}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      )}

      {canBeDownloaded(status) && status.status === 'Success' && status.links.length === 1 && (
        <>
          {status.links.map(([lang, uri]) => (
            <a
              key={lang}
              className={classNames('btn btn-icon-start btn-flat-secondary', className)}
              href={uri}
              target="_blank"
            >
              <em className="icon mx-1 text-primary">check_circle_outline</em>
              <span className="text-primary">{label}</span>
            </a>
          ))}
        </>
      )}

      {canBeDownloaded(status) && status.status === 'Success' && isEmpty(status.links) && (
        <button className={classNames('btn btn-icon-start btn-flat-secondary', className)}>
          <em className="icon mx-1 text-primary">check_circle_outline</em>
          <span className="text-primary">{label}</span>
        </button>
      )}
      {status === 'Success' && (
        <button className={classNames('btn btn-icon-start btn-flat-secondary', className)}>
          <em className="icon mx-1 text-primary">check_circle_outline</em>
          <span className="text-primary">{label}</span>
        </button>
      )}
      {status === 'Failure' && (
        <button className={classNames('btn btn-icon-start btn-flat-secondary', className)}>
          <em className="icon text-danger mx-1">block</em>
          <span className="text-primary">{label}</span>
        </button>
      )}
      {status === 'Pending' && (
        <button className={classNames('btn btn-icon-start btn-flat-secondary', className)}>
          <TinySpinner className="ms-1 me-2" />
          <span className="text-primary">{label}</span>
        </button>
      )}
    </>
  );
};

RegulationIndicator.displayName = 'RegulationIndicator';
